<template>
  <table-wrapper>
    <v-data-table-server
      class="coronet-table--highlightable table-layout-fixed"
      item-value="id"
      :show-expand="expandable"
      :headers="headers"
      :items="items"
      :items-length="total"
      :loading="loading"
      :page="pagination.page + 1"
      :items-per-page="pagination.pageSize"
      :items-per-page-options="paginationOptions"
      :sort-by="sortBy"
      @update:options="onOptionsChanged"
    >
      <template #item="{ item, internalItem, toggleExpand, isExpanded }">
        <tr class="table-row">
          <td v-if="expandable" class="expander-col">
            <v-icon
              v-if="item.hasDestinations"
              icon="$chevronDown"
              class="expand-icon"
              :class="{ rotated: isExpanded(internalItem) }"
              @click="onToggleExpand(item, internalItem, toggleExpand, isExpanded)"
            />
          </td>
          <td>
            <span class="subtitle2">{{ item.email ?? $t("general.none") }}</span>
          </td>
          <td>
            <div class="body2">{{ item.agentId ?? $t("general.none") }}</div>
            <div class="caption text-indigo-medium">
              {{ item.tunnelIp ?? $t("general.none") }}
            </div>
          </td>
          <td>
            <div class="body2">{{ formatTime(item.startTime) }}</div>
            <div class="caption text-indigo-medium">
              {{ formatDuration(item.durationSeconds) }}
            </div>
          </td>
          <td>
            <span :class="statusClass(item.status)">
              {{ $t(`network.overview.status.${item.status}`) }}
            </span>
          </td>
          <td>
            <div class="body2">{{ formatBandwidth(item.bandwidthBps) }}</div>
            <div class="caption" :class="latencyClass(item.latencyMs)">
              {{ formatLatency(item.latencyMs) }}
            </div>
          </td>
          <td class="coro-table-col--sticky-right text-right">
            <v-btn
              v-if="item.canRevoke || item.canConnect"
              rounded
              color="primary"
              size="small"
              :disabled="actionsRestricted"
              @click="
                $emit('item-action-applied', {
                  item,
                  action: item.canRevoke
                    ? NetworkConnectionAction.REVOKE
                    : NetworkConnectionAction.CONNECT,
                })
              "
            >
              {{
                item.canRevoke
                  ? $t("network.overview.table.revoke")
                  : $t("network.overview.table.connect")
              }}
            </v-btn>
          </td>
        </tr>
      </template>

      <template #expanded-row="{ item, columns }">
        <tr v-if="destinationsLoading[item.id]">
          <td :colspan="columns.length" class="pa-4">
            <v-progress-circular indeterminate size="20" width="2" color="primary" />
          </td>
        </tr>
        <template v-else>
          <tr class="destinations-header">
            <td v-if="expandable" class="expander-col"></td>
            <td class="caption text-indigo-medium">
              {{ $t("network.overview.table.destinationIp") }}
            </td>
            <td class="caption text-indigo-medium">{{ $t("network.overview.table.domain") }}</td>
            <td class="caption text-indigo-medium">{{ $t("network.overview.table.hits") }}</td>
            <td class="caption text-indigo-medium">
              {{ $t("network.overview.table.lastSeen") }}
            </td>
            <td class="caption text-indigo-medium">
              {{ $t("network.overview.table.swgStatus") }}
            </td>
            <td></td>
          </tr>
          <tr v-for="destination in destinations[item.id] ?? []" :key="destination.id">
            <td v-if="expandable" class="expander-col"></td>
            <td>
              <div class="body2">{{ destination.destinationIp ?? $t("general.none") }}</div>
              <div class="caption text-indigo-medium">{{ destination.port ?? "" }}</div>
            </td>
            <td class="body2">{{ destination.domain }}</td>
            <td class="body2">{{ destination.hits ?? $t("general.none") }}</td>
            <td class="body2">{{ formatTime(destination.lastSeen) }}</td>
            <td>
              <span :class="swgStatusClass(destination.swgStatus)">
                {{ $t(`network.overview.swg.${destination.swgStatus}`) }}
              </span>
            </td>
            <td></td>
          </tr>
          <tr v-if="!(destinations[item.id] ?? []).length">
            <td :colspan="columns.length" class="pa-4 body2 text-indigo-medium">
              {{ $t("network.overview.table.noDestinations") }}
            </td>
          </tr>
        </template>
      </template>

      <template #no-data>
        <div class="py-8 text-center body2 text-indigo-medium">
          {{ $t("network.overview.table.noData") }}
        </div>
      </template>
    </v-data-table-server>
  </table-wrapper>
</template>

<script lang="ts">
import { computed, defineComponent, type PropType } from "vue";
import { useI18n } from "vue-i18n";
import TableWrapper from "@/components/TableWrapper.vue";
import { getFormattedDateTime } from "@/_helpers/date";
import { isWorkspaceFrozenOrActionRestricted } from "@/_helpers/permissions";
import { RolePermissionsScope } from "@/constants/roles";
import { SubscriptionModule } from "@/constants/workplaces";
import { paginationOptions } from "@/constants/table";
import {
  NetworkConnectionAction,
  NetworkConnectionStatus,
  NetworkDestinationSwgStatus,
} from "@/constants/network";
import type { Pagination } from "@/types";
import type {
  NetworkConnection,
  NetworkDestination,
} from "@/_store/network/network-analytics.module";

/** Column the table opens on, and the sort the store already defaults to. */
const DEFAULT_SORT_BY = [{ key: "startTime", order: "desc" as const }];

export default defineComponent({
  components: { TableWrapper },
  props: {
    items: {
      type: Array as PropType<NetworkConnection[]>,
      default: () => [],
    },
    total: {
      type: Number,
      default: 0,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    pagination: {
      type: Object as PropType<Pagination>,
      required: true,
    },
    destinations: {
      type: Object as PropType<Record<string, NetworkDestination[]>>,
      default: () => ({}),
    },
    destinationsLoading: {
      type: Object as PropType<Record<string, boolean>>,
      default: () => ({}),
    },
    /** Whether a row may be opened to reveal its destinations. */
    expandable: {
      type: Boolean,
      default: true,
    },
  },
  emits: ["pagination-changed", "item-action-applied", "row-expanded"],
  setup(props, { emit }) {
    const i18n = useI18n();

    const actionsRestricted = computed(() =>
      isWorkspaceFrozenOrActionRestricted(
        RolePermissionsScope.PROTECTION,
        SubscriptionModule.NETWORK
      )
    );

    /**
     * Every column carries a width because the table is laid out with `table-layout: fixed`. That is what
     * stops the columns from being re-measured when a row is expanded: with automatic layout the wider
     * content of the destinations sub-table steals width from whichever column has the most slack — the
     * expander — and the whole row shifts left by a few pixels as it opens.
     */
    const headers = computed(() => [
      ...(props.expandable
        ? [{ title: "", key: "data-table-expand", sortable: false, width: 48 }]
        : []),
      { title: i18n.t("network.overview.table.email"), key: "email", width: "22%" },
      { title: i18n.t("network.overview.table.agent"), key: "agentId", width: "18%" },
      { title: i18n.t("network.overview.table.start"), key: "startTime", width: "16%" },
      { title: i18n.t("network.overview.table.status"), key: "status", width: "13%" },
      { title: i18n.t("network.overview.table.bandwidth"), key: "bandwidthBps", width: "15%" },
      { title: "", key: "actions", sortable: false, width: 140 },
    ]);

    /**
     * Vuetify pages are 1-based while the store is 0-based, and `sortBy` is an array that is empty when
     * unsorted — both are normalised here so the store only ever sees its own conventions.
     *
     * The initial sort is seeded through `:sort-by` rather than left to the store's default, because
     * `v-data-table-server` emits `update:options` during its own setup: with an unseeded table that
     * first emit carries an empty `sortBy` and would clear the default before anything was ever shown.
     */
    const onOptionsChanged = (options: {
      page: number;
      itemsPerPage: number;
      sortBy: Array<{ key: string; order?: string | boolean }>;
    }) => {
      const sort = options.sortBy?.length
        ? `${options.sortBy[0].key},${options.sortBy[0].order === "desc" ? "desc" : "asc"}`
        : undefined;
      emit("pagination-changed", {
        page: Math.max(options.page - 1, 0),
        pageSize: options.itemsPerPage,
        sort,
      });
    };

    /** Destination rows are only requested the first time a row is opened. */
    const onToggleExpand = (
      item: NetworkConnection,
      internalItem: unknown,
      toggleExpand: (row: never) => void,
      isExpanded: (row: never) => boolean
    ) => {
      if (!isExpanded(internalItem as never)) {
        emit("row-expanded", item.id);
      }
      toggleExpand(internalItem as never);
    };

    /**
     * Full date and time, through the app's moment-backed helper.
     *
     * The window runs up to 90 days, so hours and minutes alone cannot tell a session that started this
     * morning from one three months ago — and the helper is also what follows the locale the user picked,
     * where the platform's own `toLocale*` follows the browser's.
     */
    const formatTime = (value: number | null) =>
      value ? getFormattedDateTime(value) : i18n.t("general.none");

    const formatDuration = (seconds: number | null) => {
      if (!seconds && seconds !== 0) return i18n.t("general.none");
      const h = Math.floor(seconds / 3600);
      const m = Math.floor((seconds % 3600) / 60);
      const s = Math.floor(seconds % 60);
      return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
    };

    /**
     * Scaled to the unit that can actually show the value.
     *
     * Rounding straight to whole megabits reads every session below 500 kbps as "0", which is an ordinary
     * VPN session reported as an idle one.
     */
    const formatBandwidth = (bps: number | null) => {
      if (bps === null || bps === undefined) return i18n.t("general.none");
      if (bps < 1_000_000) {
        return i18n.t("network.overview.units.kbitPerSecond", { n: Math.round(bps / 1_000) });
      }
      return i18n.t("network.overview.units.mbitPerSecond", { n: (bps / 1_000_000).toFixed(1) });
    };

    /** Latency has no source in the platform yet, so a missing value is expected rather than an error. */
    const formatLatency = (ms: number | null) =>
      ms === null || ms === undefined
        ? i18n.t("general.none")
        : i18n.t("network.overview.units.milliseconds", { n: ms });

    const latencyClass = (ms: number | null) => {
      if (ms === null || ms === undefined) return "text-indigo-medium";
      if (ms < 30) return "text-green-dark";
      return ms < 80 ? "text-orange-dark" : "text-red-dark";
    };

    const statusClass = (value: NetworkConnectionStatus) =>
      value === NetworkConnectionStatus.ACTIVE ? "text-green-dark" : "text-indigo-medium";

    const swgStatusClass = (value: NetworkDestinationSwgStatus) =>
      value === NetworkDestinationSwgStatus.BLOCKED ? "text-red-dark" : "text-green-dark";

    return {
      headers,
      sortBy: DEFAULT_SORT_BY,
      paginationOptions,
      actionsRestricted,
      onOptionsChanged,
      onToggleExpand,
      formatTime,
      formatDuration,
      formatBandwidth,
      formatLatency,
      latencyClass,
      statusClass,
      swgStatusClass,
      NetworkConnectionAction,
    };
  },
});
</script>

<style scoped lang="scss">
// Fixed layout means the column widths come from the header row alone, so adding or removing rows -
// which is exactly what expanding a row does - cannot change them.
:deep(.table-layout-fixed) {
  table {
    table-layout: fixed;
  }

  // Cells no longer stretch to fit, so anything long has to be trimmed rather than allowed to overflow
  // into the neighbouring column.
  td {
    overflow: hidden;
    text-overflow: ellipsis;
  }

  // Under fixed layout the header row alone decides the widths, so this is a fallback for the case
  // where the header has not rendered yet rather than the mechanism itself.
  td.expander-col {
    width: 48px;
    min-width: 48px;
  }
}

.expand-icon {
  cursor: pointer;
  transition: transform 0.2s ease;

  &.rotated {
    transform: rotate(180deg);
  }
}

.destinations-header td {
  padding-top: 12px;
}
</style>
